import { useEffect, useState } from 'preact/hooks'
import cn from 'classnames'
import styles from './AddToCartButton.module.scss'
import UnmanagedDom from './UnmanagedDom'

export type Size = 'small' | 'large'

type ProductInfo = {
    longSku: string
    id: string
}

export type AddToCartButtonProps = {
    product: ProductInfo
    size?: Size
    class?: string
}

let instanceCount = 0 // Used to create a unique id for the button

export default function AddToCartButton({
    class: className,
    product,
    size = 'large'
}: AddToCartButtonProps) {
    const [elementId, setElementId] = useState<string | null>(null)

    // default to large buttons
    const buttonSize = size === 'small' ? 'small' : 'large'

    useEffect(() => {
        if (elementId) {
            initNAPIButton(
                elementId,
                product?.longSku || product.id,
                buttonSize
            )
        } else {
            setElementId(
                [
                    'a2c',
                    (product?.longSku || product.id).replace(/(\s|\W)/g, ''),
                    instanceCount++
                ].join('-')
            )
        }
    }, [elementId])

    const initialButtonClassName =
        buttonSize === 'large' ? 'AddToBagButtonLarge' : 'AddToBagButtonSmall'

    return (
        <span
            class={cn(
                styles.addToCart,
                className,
                buttonSize === 'large' ? styles.large : undefined
            )}
            id={elementId as string}
            onClick={decorateEvent}>
            {elementId ? null : (
                <UnmanagedDom>
                    <button
                        class={cn('AddToBagButton', initialButtonClassName)}
                        type="button"
                        disabled>
                        <span class="VisuallyHidden">Loading</span>
                        &nbsp;
                    </button>
                </UnmanagedDom>
            )}
        </span>
    )
}

/**
 * Due to the way the NAPI Button works, we cannot use normal event propagation
 * control, e.g. event.stopPropagation etc.
 * Instead, the event needs to be excluded when handling within a parent e.g. a ProductCard
 */
export function isAddToCartEvent(event: Event): boolean {
    // @ts-ignore
    return event.__isAddToCard__ ?? false
}

function decorateEvent(event: Event) {
    // @ts-ignore
    event.__isAddToCard__ = true
}

/**
 * Init the button through the NAPI, which actually renders the DOM
 */
async function initNAPIButton(
    elementId: string,
    id: string,
    buttonSize: Size
): Promise<void> {
    if (!window.napi) return

    try {
        await window.napi.getConfig()
    } catch (error) {
        console.error('NAPI getConfig error', error)
        return
    }

    window.ui = window.ui || []
    window.ui.push({
        id: elementId,
        module: 'AddToBagButton',
        configuration: { props: { productId: id, buttonSize } },
        ecommerceData: { activated: true }
    })
}
